import React from 'react';

import {
	Box,
	Form,
	type FormToggle
} from '@ekhodealer/ekho-common/rev';

import type { Section, SettingsGroup, Subsection } from '@ekhodealer/ekho-common';
import type {
	RichDescriptionProps} from '@ekhodealer/ekho-common/rev';
import type { z } from 'zod';

import { SettingsSection } from '@common/components/SettingsSection';

export interface FormSectionProps {
	sectionPath: {
		group: SettingsGroup;
		section: Section;
		subsection?: Subsection;
	};
	form: {
		schema: z.ZodType;
		defaultValues?: Record<string, unknown>;
		onSubmit: (
			data: z.infer<z.ZodType>,
		) => { isSuccess: boolean } | Promise<{ isSuccess: boolean }>;
	};
	/** Title shown in the SettingsSection header (left column) */
	title?: string;
	/** Description shown in the SettingsSection header (left column) */
	description?: string | RichDescriptionProps;
	/** Title shown inside the gray box form (right column). Must differ from `title`. */
	formTitle?: string;
	/** Description shown inside the gray box form. Only used when formTitle is provided. */
	formDescription?: string | RichDescriptionProps;
	children?: React.ReactNode;
	toggleSettings?: FormToggle<z.ZodType>;
	isEkhoOnly?: boolean;
	saveMode?: 'auto' | 'manual';
	useCard?: boolean;
	noBackground?: boolean;
	hideDivider?: boolean;
}
/**
 * Section component - represents a form section within Sales settings.
 * Wraps content in SettingsSection (title/description on left, gray box on right).
 * The gray box contains a Form with its own distinct title.
 */
export function FormSection({
	sectionPath,
	form,
	title,
	description,
	formTitle,
	formDescription,
	children,
	toggleSettings,
	isEkhoOnly = false,
	// eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO: will be used for modal behavior (saveMode = manual)
	saveMode = 'auto',
	noBackground = false,
	hideDivider = false,
}: FormSectionProps) {
	// Description for SettingsSection must be a string or ReactNode, not RichDescriptionProps
	const sectionDescription = typeof description === 'string' ? description : undefined;

	const formContent = (
		<Form
			name={`${sectionPath.group}-${sectionPath.section}${
				sectionPath.subsection ? `-${sectionPath.subsection}` : ''
			}`}
			schema={form.schema}
			defaultValues={form.defaultValues}
			onSubmit={form.onSubmit}
			title={formTitle}
			toggle={toggleSettings}
			titleLevel='subgroup'
			titleWeight='semibold'
			description={formDescription}
			tag={isEkhoOnly ? { children: 'Ekho only', accent: 1 } : undefined}
			showSaveAndClear>
			{children}
		</Form>
	);

	const boxContent = noBackground ? (
		formContent
	) : (
		<Box
			padding='24'
			background='secondary'
			rounding='12'
			showOverflow>
			{formContent}
		</Box>
	);

	if (!title) {
		return boxContent;
	}

	return (
		<SettingsSection
			title={title}
			description={sectionDescription}
			hideDivider={hideDivider}>
			{boxContent}
		</SettingsSection>
	);
}
